import React, { useState, useEffect } from 'react';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import { Card, message, App } from 'antd';
import { useNavigate } from 'react-router-dom';
import 'react-big-calendar/lib/css/react-big-calendar.css';

const localizer = momentLocalizer(moment);

// Custom calendar theme styles
const calendarTheme = `
  .rbc-calendar {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  }
  
  .rbc-header {
    background-color: #f0f5ff;
    color: #2E86AB;
    font-weight: 600;
    border: 1px solid #d9d9d9;
    padding: 8px;
  }
  
  .rbc-today {
    background-color: #e6f7ff;
  }
  
  .rbc-off-range {
    color: #bfbfbf;
  }
  
  .rbc-month-view {
    border: 1px solid #d9d9d9;
  }
  
  .rbc-date-cell {
    padding: 4px;
  }
  
  .rbc-date-cell button {
    color: #262626;
    font-weight: 500;
  }
  
  .rbc-date-cell.rbc-off-range button {
    color: #bfbfbb;
  }
  
  .rbc-toolbar {
    background-color: #fafafa;
    padding: 16px;
    border-radius: 8px;
    margin-bottom: 16px;
    border: 1px solid #d9d9d9;
  }
  
  .rbc-toolbar button {
    background-color: #fff;
    border: 1px solid #d9d9d9;
    color: #2E86AB;
    padding: 4px 12px;
    border-radius: 4px;
    font-weight: 500;
  }
  
  .rbc-toolbar button:hover {
    background-color: #e6f7ff;
    border-color: #40a9ff;
  }
  
  .rbc-toolbar button.rbc-active {
    background-color: #2E86AB;
    border-color: #2E86AB;
    color: white;
  }
  
  .rbc-toolbar .rbc-toolbar-label {
    color: #2E86AB;
    font-weight: 600;
    font-size: 18px;
  }
`;

const CampaignCalendar = ({ campaigns = [] }) => {
  const { message: messageApi } = App.useApp();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);

  // Convert campaigns to calendar events
  const convertCampaignsToEvents = (campaignData) => {
    const calendarEvents = campaignData
      .filter(campaign => campaign.startDate && campaign.endDate)
      .map(campaign => ({
        id: campaign._id,
        title: campaign.name,
        start: new Date(campaign.startDate),
        end: new Date(campaign.endDate),
        resource: campaign,
        // Add status-based styling
        className: `campaign-event campaign-${campaign.status || 'draft'}`
      }));
    
    setEvents(calendarEvents);
  };

  // Handle event double-click navigation
  const handleSelectEvent = (event) => {
    const campaign = event.resource;
    
    // Navigate based on campaign status
    if (campaign.status === 'active' || campaign.status === 'completed') {
      // If campaign is active or completed, go to report page
      navigate(`/dashboard/campaigns/report/${campaign._id}`);
    } else {
      // If campaign is draft, paused, tuning, or cancelled, go to edit page
      navigate(`/dashboard/campaigns/edit/${campaign._id}`);
    }
  };

  // Get event style based on campaign status
  const eventStyleGetter = (event) => {
    const campaign = event.resource;
    let backgroundColor = '#2E86AB'; // default theme blue
    let textColor = 'white';
    
    switch (campaign.status) {
      case 'active':
        backgroundColor = '#52c41a'; // success green
        break;
      case 'completed':
        backgroundColor = '#1890ff'; // primary blue
        break;
      case 'paused':
        backgroundColor = '#faad14'; // warning orange
        break;
      case 'cancelled':
        backgroundColor = '#ff4d4f'; // error red
        break;
      default: // draft
        backgroundColor = '#8c8c8c'; // neutral gray
    }

    return {
      style: {
        backgroundColor,
        borderRadius: '6px',
        opacity: 0.9,
        color: textColor,
        border: 'none',
        display: 'block',
        fontSize: '12px',
        fontWeight: '500',
        padding: '2px 4px',
        boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
        transition: 'all 0.2s ease'
      }
    };
  };

  useEffect(() => {
    convertCampaignsToEvents(campaigns);
  }, [campaigns]);

  return (
    <>
      <style>{calendarTheme}</style>
      <Card 
        title="Campaign Calendar" 
        style={{ height: '600px' }}
        bodyStyle={{ height: 'calc(100% - 56px)', padding: '16px' }}
      >
        <div style={{ height: '100%' }}>
          <Calendar
            localizer={localizer}
            events={events}
            startAccessor="start"
            endAccessor="end"
            style={{ height: '100%' }}
            onDoubleClickEvent={handleSelectEvent}
            eventPropGetter={eventStyleGetter}
            views={['month', 'week', 'day']}
            defaultView="month"
            popup
            tooltipAccessor={(event) => `${event.title} (${event.resource.status || 'draft'})`}
            dayPropGetter={(date) => ({
              style: {
                backgroundColor: moment(date).isSame(moment(), 'day') ? '#e6f7ff' : 'white'
              }
            })}
            messages={{
              next: "Next",
              previous: "Previous", 
              today: "Today",
              month: "Month",
              week: "Week",
              day: "Day",
              agenda: "Agenda",
              date: "Date",
              time: "Time",
              event: "Campaign",
              noEventsInRange: "No campaigns in this date range.",
              showMore: total => `+${total} more campaigns`
            }}
          />
        </div>
      </Card>
    </>
  );
};

export default CampaignCalendar;